/**
 * Raise a task — Step 46.
 *
 * A task is a TICKET of type `task`, and this is deliberately the smallest
 * form in the product. Kai: *"I want tasks to appear as tickets, with a group
 * and assignee, but pretty much nothing else"*, and *"someone should be able
 * to create a task ticket to do something unrelated to a ticket."*
 *
 * So: what, who does it, and optionally which ticket it belongs to. No
 * priority picker, no source, no urgency/impact, no custom fields. Those all
 * exist on the ticket and are all reachable afterwards — putting them here
 * would make raising a task as much work as raising an incident, which is the
 * exact friction that has people skipping it.
 *
 * The REQUESTER is whoever raises it. A task is work you are asking a
 * colleague for, so "who wants this?" has an obvious answer and asking would
 * be a question with one sensible response.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { qk } from '../../lib/queryClient';
import { useAuth } from '../../lib/AuthContext';
import { useDirty } from '../../lib/dirty';
import { ticketsApi } from '../../features/tickets/api';
import { escapeHtml } from '../../lib/escapeHtml';

export function NewTaskPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  // URL state on purpose: `groupId` here is an unsaved DRAFT FIELD on a create
  // form, not a filter. Putting a half-typed ticket's group in the address bar
  // would make an abandoned draft shareable and survive a refresh that is
  // meant to discard it. (The audit matches the name, and the name is the
  // same one the queue uses for a real filter.)
  const [groupId, setGroupId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');

  const groups = useQuery({
    queryKey: qk.orgGroups,
    queryFn: ticketsApi.groups,
    staleTime: 5 * 60_000,
  });
  const people = useQuery({
    queryKey: qk.people,
    queryFn: ticketsApi.people,
    staleTime: 5 * 60_000,
  });

  const dirty = !!title.trim() || !!detail.trim();
  const clearDirty = useDirty(dirty);

  /** Only this group's members, the same rule the queue and the ticket page
   *  enforce — the API refuses an agent who is not a member. */
  const members = groups.data?.entries.find((g) => g.id === groupId)?.memberIds ?? [];
  const assignable = (people.data?.entries ?? []).filter((p) => members.includes(p.id));

  const create = useMutation({
    mutationFn: () =>
      ticketsApi.create({
        subject: title.trim(),
        type: 'task',
        priority: 'medium',
        requesterEmail: me!.user.email,
        bodyHtml: `<p>${escapeHtml(detail.trim() || title.trim())}</p>`,
        ...(groupId ? { groupId } : {}),
        ...(assigneeId ? { assigneeId } : {}),
      }),
    onSuccess: (r) => {
      if (!r.ok || !r.body) return toast.error(r.error ?? 'Could not raise that task.');
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.tickets.all });
      navigate(`/tickets/${r.body.id}`);
    },
  });

  return (
    <div className="mx-auto max-w-xl p-6">
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">New task</h1>
      </div>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">What needs doing</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Detail <span className="font-normal">— optional</span>
          </label>
          <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Group</label>
            <Select
              value={groupId}
              onChange={(e) => {
                setGroupId(e.target.value);
                // Changing group strands the agent, and the API refuses that
                // — cleared here so the form cannot build a state it will be
                // told off for.
                setAssigneeId('');
              }}
            >
              <option value="">No group</option>
              {(groups.data?.entries ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Assign to</label>
            <Select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              disabled={!groupId || assignable.length === 0}
            >
              <option value="">Unassigned</option>
              {assignable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name ?? p.email}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {groupId ? 'Members of the chosen group.' : 'Pick a group first.'}
            </p>
          </div>
        </div>
        {/* No confirm of its own — Cancel navigates, and navigation is what
            the one router-level dirty guard intercepts. Asking here as well
            is what produced Kai's *"double discard"*. */}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="ghost" onClick={() => navigate('/tickets')}>
            Cancel
          </Button>
          <Button type="submit" disabled={!title.trim() || create.isPending}>
            {create.isPending ? 'Raising…' : 'Raise task'}
          </Button>
        </div>
      </form>
    </div>
  );
}
